import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface DeleteTarget {
  id: string;
  type: 'session' | 'unit' | 'item';
  name: string;
  /** Cascade counts, resolved by the caller from the loaded session detail. */
  unitCount: number;
  itemCount: number;
}

const COPY = {
  session: { title: 'verification.delete.sessionTitle', message: 'verification.delete.sessionMessage' },
  unit: { title: 'verification.delete.unitTitle', message: 'verification.delete.unitMessage' },
  item: { title: 'verification.delete.itemTitle', message: 'verification.delete.itemMessage' },
} as const;

interface DeleteConfirmDialogProps {
  target: DeleteTarget | null;
  onConfirm: () => void;
  onClose: () => void;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  target,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={target !== null} onOpenChange={(next) => !next && onClose()}>
      {/* This one carried neither fullWidth nor an explicit maxWidth, so it stays
          content-sized under the default 600px ceiling. */}
      {target && (
        <DialogContent
          className="revy-ui w-auto max-w-[calc(100%-4rem)] sm:max-w-[600px]"
          aria-describedby={undefined}
        >
          <DialogHeader>
            <DialogTitle className="revy-cjk text-lg leading-[1.5]">
              {t(COPY[target.type].title)}
            </DialogTitle>
          </DialogHeader>

          <p className="revy-cjk leading-[1.5]">
            {t(COPY[target.type].message, {
              name: target.name,
              unitCount: target.unitCount,
              itemCount: target.itemCount,
            })}
          </p>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={onClose}>
              <span className="revy-cjk">{t('common.cancel')}</span>
            </Button>
            <Button variant="destructive" onClick={onConfirm}>
              <span className="revy-cjk">{t('common.delete')}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default DeleteConfirmDialog;
